'use client';

import React, { createContext, useContext, useEffect, useState } from 'react';
import { DEFAULT_LOCALE, LOCALE_COOKIE, isLocale, type Locale } from './config';
import { getDictionary, type Dictionary } from './dictionaries';

interface LocaleContextValue {
  locale: Locale;
  t: Dictionary;
  setLocale: (locale: Locale) => void;
}

const LocaleContext = createContext<LocaleContextValue | null>(null);

/** The visitor's saved choice, else their browser language. Mirrors LOCALE_DETECT_SCRIPT in config.ts. */
function detectLocale(): Locale {
  const saved = document.cookie.match(new RegExp(`(?:^|; )${LOCALE_COOKIE}=([^;]+)`))?.[1];
  if (isLocale(saved)) return saved;
  return (navigator.language || '').toLowerCase().startsWith('nl') ? 'nl' : DEFAULT_LOCALE;
}

export function LocaleProvider({ children }: { children: React.ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(DEFAULT_LOCALE);

  useEffect(() => {
    setLocaleState(detectLocale());
  }, []);

  useEffect(() => {
    document.documentElement.lang = locale;
    document.documentElement.classList.remove('locale-pending');
  }, [locale]);

  const setLocale = (next: Locale) => {
    if (next === locale) return;
    document.cookie = `${LOCALE_COOKIE}=${next}; path=/; max-age=${60 * 60 * 24 * 365}; samesite=lax`;
    setLocaleState(next);
  };

  return (
    <LocaleContext.Provider value={{ locale, t: getDictionary(locale), setLocale }}>
      {children}
    </LocaleContext.Provider>
  );
}

export function useI18n() {
  const value = useContext(LocaleContext);
  if (!value) throw new Error('useI18n must be used inside LocaleProvider');
  return value;
}

/** Shows `en` or `nl` depending on the visitor's language. Lets server components pass both versions. */
export function Localized({ en, nl }: { en: React.ReactNode; nl: React.ReactNode }) {
  const { locale } = useI18n();
  return <>{locale === 'nl' ? nl : en}</>;
}
